import React, { useEffect, useState } from 'react';
import { useBrand } from '@/context/BrandContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { EditableText } from '../ui/EditableText';
import { BrandSectionLayout } from './BrandSectionLayout';
import { supabase } from '@/lib/supabase';
import { hexToRgbString, hexToHslString, generateShades, generateTints } from '@/lib/colorUtils';

interface ColorPalettesSectionProps {
  section: any;
  isEditing: boolean;
  isGlobalDarkMode: boolean;
  editingElement: string | null;
  setEditingElement: (element: string | null) => void;
  updateSectionContent: (key: string, value: string) => void;
  updateSectionStyles: (key: string, styles: any) => void;
  pageNumber?: number;
}

interface ColorInfo {
  name: string;
  hex: string;
  variable: string;
  format: 'hex' | 'rgb' | 'hsl';
  shadesEnabled?: boolean;
  shadesCount?: number;
  customShades?: Array<{ hex: string; name: string; variable: string }>;
  tintsEnabled?: boolean;
  tintsCount?: number;
  customTints?: Array<{ hex: string; name: string; variable: string }>;
}

interface ColorPalette {
  name: string;
  baseColor: string;
  shades: string[];
  tints: string[];
  rgb: string;
  hsl: string;
}

const ShadeBlock: React.FC<{ hex: string }> = ({ hex }) => {
  const isLight = parseInt(hex.slice(1, 3), 16) > 128;
  return (
    <div
      className="h-full flex items-center justify-center text-sm font-mono"
      style={{
        backgroundColor: hex,
        color: isLight ? '#000000' : '#FFFFFF'
      }}
    >
      {hex}
    </div>
  );
};

const BaseColorBlock: React.FC<{ palette: ColorPalette }> = ({ palette }) => {
  const isLight = parseInt(palette.baseColor.slice(1, 3), 16) > 128;
  return (
    <div
      className="h-full flex flex-col justify-center items-center p-4"
      style={{
        backgroundColor: palette.baseColor,
        color: isLight ? '#000000' : '#FFFFFF'
      }}
    >
      <h4 className="text-xl font-bold mb-3">{palette.name}</h4>
      <div className="flex flex-col gap-1 text-xs">
        <div className="flex gap-2">
          <span className="font-semibold">HEX</span>
          <span className="font-mono">{palette.baseColor}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold">RGB</span>
          <span className="font-mono">{palette.rgb}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold">HSL</span>
          <span className="font-mono">{palette.hsl}</span>
        </div>
      </div>
    </div>
  );
};

const ColorColumn: React.FC<{ palette: ColorPalette }> = ({ palette }) => {
  return (
    <div className="h-full flex flex-col">
      {palette.shades.map((shade, idx) => (
        <div key={`shade-${idx}`} className="flex-1">
          <ShadeBlock hex={shade} />
        </div>
      ))}
      <div className="flex-[2]">
        <BaseColorBlock palette={palette} />
      </div>
      {palette.tints.map((tint, idx) => (
        <div key={`tint-${idx}`} className="flex-1">
          <ShadeBlock hex={tint} />
        </div>
      ))}
    </div>
  );
};

export const ColorPalettesSection: React.FC<ColorPalettesSectionProps> = ({
  section,
  isEditing,
  isGlobalDarkMode,
  editingElement,
  setEditingElement,
  updateSectionContent,
  updateSectionStyles,
  pageNumber = 1
}) => {
  const { state } = useBrand();
  const { currentWorkspace } = useWorkspace();
  const [palettes, setPalettes] = useState<ColorPalette[]>([]);

  useEffect(() => {
    const loadColorPalettes = async () => {
      if (!state.currentBrandId) {
        setDefaultPalettes();
        return;
      }

      try {
        const { data: brand, error } = await supabase
          .from('brands')
          .select('color_preset_id')
          .eq('id', state.currentBrandId)
          .maybeSingle();

        if (error) throw error;

        if (brand?.color_preset_id) {
          const { data: template, error: templateError } = await supabase
            .from('color_preset_templates')
            .select('*')
            .eq('id', brand.color_preset_id)
            .maybeSingle();

          if (templateError) throw templateError;

          if (template && template.colors && template.colors.length > 0) {
            const colors = template.colors as ColorInfo[];
            processColors(colors);
            return;
          }
        }

        setDefaultPalettes();
      } catch (error) {
        console.error('Error loading color palettes:', error);
        setDefaultPalettes();
      }
    };

    loadColorPalettes();
  }, [state.currentBrandId, state.brandData, currentWorkspace]);

  const processColors = (colors: ColorInfo[]) => {
    const newPalettes: ColorPalette[] = [];

    const colorsToPalette = colors.slice(0, 3);

    colorsToPalette.forEach((color) => {
      if (color.hex && color.hex !== '') {
        // Check if custom shades exist, otherwise generate them
        let shades: string[];
        if (color.customShades && color.customShades.length > 0) {
          // Use custom shades from the color preset
          shades = color.customShades.map(shade => shade.hex.toUpperCase());
        } else {
          // Generate shades if not available
          const shadesCount = color.shadesCount || 3;
          shades = generateShades(color.hex, shadesCount).reverse();
        }

        // Check if custom tints exist, otherwise generate them
        let tints: string[];
        if (color.customTints && color.customTints.length > 0) {
          // Use custom tints from the color preset
          tints = color.customTints.map(tint => tint.hex.toUpperCase());
        } else {
          // Generate tints if not available
          const tintsCount = color.tintsCount || 3;
          tints = generateTints(color.hex, tintsCount);
        }

        newPalettes.push({
          name: color.name,
          baseColor: color.hex.toUpperCase(),
          shades: shades,
          tints: tints,
          rgb: hexToRgbString(color.hex),
          hsl: hexToHslString(color.hex),
        });
      }
    });

    setPalettes(newPalettes);
  };

  const setDefaultPalettes = () => {
    const colors: ColorInfo[] = [
      {
        name: 'Primary',
        hex: state.brandData?.colors?.primary || '#FF6B6B',
        variable: '--primary',
        format: 'hex'
      },
      {
        name: 'Secondary',
        hex: state.brandData?.colors?.secondary || '',
        variable: '--secondary',
        format: 'hex'
      },
      {
        name: 'Accent',
        hex: state.brandData?.colors?.accent || '',
        variable: '--accent',
        format: 'hex'
      },
    ];
    processColors(colors);
  };

  if (palettes.length === 0) {
    return null;
  }

  return (
    <BrandSectionLayout
      section={section}
      isEditing={isEditing}
      isGlobalDarkMode={isGlobalDarkMode}
      editingElement={editingElement}
      setEditingElement={setEditingElement}
      updateSectionContent={updateSectionContent}
      updateSectionStyles={updateSectionStyles}
      pageNumber={pageNumber}
    >
      <div className="h-full flex gap-16">
        {/* Left Column - 30% width with title and description */}
        <div className="flex flex-col" style={{ width: '30%' }}>
          <div className="flex flex-col items-start gap-4">
            <EditableText
              value={section.content.paletteTitle || 'Color Palette'}
              onChange={(value) => updateSectionContent('paletteTitle', value)}
              element="h3"
              className="text-3xl leading-tight tracking-tight font-bold mb-8"
              placeholder="Color Palette"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'paletteTitle'}
              onEditStart={() => setEditingElement('paletteTitle')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('paletteTitle', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTitleFont || 'Inter',
                fontWeight: 'bold',
                ...section.content.paletteTitleStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />

            <EditableText
              value={section.content.paletteDescription || 'Our color palette includes carefully crafted shades and tints for each brand color, providing flexibility for various design applications.'}
              onChange={(value) => updateSectionContent('paletteDescription', value)}
              element="p"
              className="text-lg leading-relaxed"
              placeholder="Our color palette includes carefully crafted shades and tints"
              multiline={true}
              editModeEnabled={isEditing}
              isEditing={isEditing && editingElement === 'paletteDescription'}
              onEditStart={() => setEditingElement('paletteDescription')}
              onEditEnd={() => setEditingElement(null)}
              onStyleChange={(styles) => updateSectionStyles('paletteDescription', styles)}
              initialStyles={{
                fontFamily: state.brandData?.globalTextFont || 'Inter',
                ...section.content.paletteDescriptionStyles
              }}
              isGlobalDarkMode={isGlobalDarkMode}
              sectionBackgroundColor={section.customization?.backgroundColor || section.styles.backgroundColor}
            />
          </div>
        </div>

        {/* Right Column - 70% width with color palette columns */}
        <div style={{ width: '70%' }}>
          <div className="h-full flex gap-0">
            {palettes.map((palette, idx) => (
              <div key={idx} className="flex-1">
                <ColorColumn palette={palette} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </BrandSectionLayout>
  );
};
